// What every screen of Me can reach: the API, the parts and the host's extensions.

import type { AnyRoute } from "@tanstack/react-router";
import type { AgentDeviceDTO } from "@trameria/core/agent-chat-shared";
import {
  type ComponentType,
  createContext,
  type ReactNode,
  useContext,
} from "react";
import type { InstructionSigner } from "../agent-chat/signing";
import type { Api } from "../data/api";
import type { QueryPersister } from "../data/persist";
import type { AccountPlace } from "../me/account";
import type { ListMemory } from "../shell/list-memory";
import type { ShellRail } from "../shell/outside/contract";
import type { ShellHost } from "../shell/outside/outside";
import type { DockExtension, PhoneKit } from "../shell/phone";
import type { SheetState } from "../shell/shortcut-sheet";
import type { TitleBarSource } from "../shell/title-bar";
import type { MenuItem } from "../ui/menu";
import type { UiHostAction } from "../ui-fence/host-actions";
import type { DeviceDecor } from "./device-decor";
import type {
  FavoriteKind,
  HeaderAction,
  NavItem,
  NavSection,
  RowMark,
  ScreenPart,
  SettingsItem,
  SideViewProps,
} from "./screen-part";
import type { WorkspaceFilter } from "./workspace-filter";

/** Rows a host adds to a settings item's box, and their search anchors. */
export interface AppearanceRows {
  Rows: ComponentType;
  sections?: SettingsItem["sections"];
}

/** What cloud adds around Me. The offline edition passes none of these. */
/**
 * The workspaces' rows the screen shows beside Me's when Me's server is not
 * the workspaces' (a Hub's desktop: they come from the cloud with the PC's own
 * sign-in). Paths on the same API.
 */
export interface WorkspaceSource {
  /** GET → `{ tied }`: whether there is a cloud account to read them from. */
  state: string;
  /** A stream of the Inbox's workspace rows (`inbox` events, `{ items }`). */
  inbox: string;
  /** Search (`?q=&types=&limit=`), answering as `/api/search`. */
  search: string;
}

/**
 * Me living somewhere else than the server this screen talks to (the cloud's
 * web while Me is on the person's Hub, a Hub that keeps only a copy): Me's
 * pages show `Cover` instead (the settings stay, and the pages `stays` names),
 * and the sidebar leaves Me's sections out.
 */
export interface MeAway {
  /** Whether Me is away now (a hook: it reads the host's own query). */
  useAway(): boolean;
  Cover: ComponentType;
  /**
   * Pages under /me that are this server's own, not personal data (the
   * cloud's direct messages): shown as they are while Me is away.
   */
  stays?(path: string): boolean;
  /**
   * Whether Me's sidebar leaves out what is drawn from Me's data (its
   * favorites and sections); default `useAway`. A host whose pages give way
   * while the data is still its own (shown by another screen) says no here.
   */
  useSectionsAway?(): boolean;
  /** A word over Me's pages while Me is not away (moving: read-only). */
  Note?: ComponentType;
}

/** Another sidebar around Me's pages (`MeExtensions.useSidebar`). */
export interface MeSidebarSwap {
  sidebar: ReactNode;
  /** The same folded to its icons. */
  collapsed: ReactNode;
  /** Read aloud for the sidebar when it is a drawer. */
  label: string;
}

export interface MeExtensions {
  /** The rail (Workspaces). Without it the shell has no rail at all. */
  Rail?: ComponentType;
  /** Me living elsewhere (see MeAway). */
  meAway?: MeAway;
  /** Items appended to the account menu (sign out, …). */
  accountMenu?(): MenuItem[];
  /** The workspaces' rows from elsewhere (a Hub's desktop). */
  workspace?: WorkspaceSource;
  /**
   * Opens a link to another place (a Workspace) in its own window or tab.
   * Returns true when it did; false lets the shell navigate here.
   */
  openTarget?(href: string, place: string): boolean;
  /** 401 from the server (cloud: send the person to sign in). */
  onUnauthorized?(): void;
  /** Routes outside /me (cloud's /{workspace}/...). */
  routes?(root: AnyRoute): AnyRoute[];
  /**
   * Which of the host's routes (by full path, "/$slug/topics") have their
   * screens loaded ahead while idle, as Me's are (app/lazy.tsx). Default none.
   */
  warmRoutes?(fullPath: string): boolean;
  /** Where to keep the query cache across reloads (cloud: always IndexedDB). */
  persister?: QueryPersister;
  /** Settings items of the host (sign-in and security, devices, …). */
  settings?: readonly SettingsItem[];
  /**
   * Signs what the person sends to an agent (the app's own screen and a
   * Vault's screen in the app only; a browser has none). ../agent-chat/signing.ts
   */
  instructions?: InstructionSigner;
  /**
   * Where the personal data this screen shows is kept, said after each
   * personal list's title (shell/panel.tsx's `PersonalMark`). Absent: not said.
   */
  personalPlace?: "vault" | "cloud";
  /**
   * This PC's device in the list the screen reads, where that list has no
   * `this_pc` (the desktop app showing Personal data kept in Cloud). null:
   * this PC has none there. ../agent-chat/this-pc.tsx
   */
  thisDevice?: () => Promise<string | null>;
  /**
   * Rows the host adds at the end of Appearance's box (cloud: the home screen
   * icon), with their anchors for the settings search.
   */
  appearanceRows?: AppearanceRows;
  /**
   * Where the account's name and picture live (how the person shows in the
   * workspaces; me/account.ts): `profile` (cloud), a source (a Hub tied to a
   * cloud account). Absent: there is none, and General has neither (the
   * offline edition on its own: no one else sees its owner).
   */
  account?: AccountPlace;
  /**
   * The settings' name and sentence, at the top of their sidebar and index
   * (cloud: the account's settings, beside each workspace's). Default
   * "Settings" and a sentence of one's own.
   */
  settingsTitle?(): string;
  settingsDescription?(): string;
  /**
   * The app's own settings (the items of group `app`) live in a window of
   * their own (the desktop's Settings…): they are not listed here, and one
   * row opens that window instead.
   */
  appSettings?: { open(path?: string): void; description?(): string };
  /**
   * Where a device's agent settings are, when the host keeps them in an item
   * of its own (the desktop app: the device list, in the app's settings).
   * Without it, the agents' item (`/me/settings/agents`).
   */
  agentSettingsPath?(device: AgentDeviceDTO | null): string;
  /**
   * This screen is that window: only the items of group `app`, and leaving
   * the settings closes it.
   */
  settingsWindow?: { close(): void };
  /**
   * In place of the error screen, for an error the host knows (the desktop
   * app signed out of the cloud that keeps the personal data): what to say
   * and the way out. Null: the usual error screen.
   */
  errorScreen?(error: unknown): ReactNode | null;
  /**
   * Shown inside another screen (the cloud's page in the desktop app's
   * frame): a hook for how much of the frame to draw now (null: all of it).
   * The panel alone, beside the app's own sidebar.
   */
  frameLayout?: {
    use(): { kind: "panel" } | null;
  };
  /**
   * Host actions a ui fence may call besides ui-core's built-ins (cloud's
   * Issues adds createIssue). Names must not repeat a built-in.
   */
  uiHostActions?: readonly UiHostAction[];
  /**
   * Window controls drawn over the page's top-left corner (cloud in the mac
   * desktop app). The frame keeps that corner free and the strip draggable.
   */
  titleBar?: TitleBarSource;
  /**
   * How the host shows devices it runs itself (cloud: its sandboxes) in the
   * device list, the device's detail and the chat's device pill, and the ones
   * it can make on the spot.
   */
  deviceDecor?: DeviceDecor;
  /**
   * The phone's screens (`@trameria/ui-core/phone`): the Dock and its menu at
   * a phone's width. Without them a phone gets the drawer.
   */
  phone?: PhoneKit;
  /**
   * How the personal datasets are read here. A Vault's screens: a ui's
   * queries are worked out in the Vault and only their results come
   * (`queryHere`), and there are no write tokens (`writeTokens: false`:
   * nothing outside reaches the Vault). Default: rows kept here, tokens on.
   */
  datasets?: { queryHere?: boolean; writeTokens?: boolean };
  /** The host's place on the Dock's right (cloud: a workspace). */
  dock?: DockExtension;
  /**
   * The rail of places as data (cloud: Me, the workspaces, "new"): the rail
   * (`Rail`) draws it, and a frame drawn outside gets it (shell/outside).
   * A hook, the same one for the app's life.
   */
  useRail?(): ShellRail | null;
  /**
   * The sidebar Me's page is shown with in place of Me's own (cloud: a
   * workspace's, while its rows of my own things are open in it), or null
   * for Me's. A hook, the same one for the app's life.
   */
  useSidebar?(): MeSidebarSwap | null;
  /**
   * What draws the frame outside the screens (a native app's web view): the
   * menu and the headers go to it as data (shell/outside). When it says the
   * screens are embedded, they draw neither the sidebar nor header rows.
   */
  shellHost?: ShellHost;
}

export interface MeApp {
  api: Api;
  /** The API's prefix, for URLs the browser loads itself (frames, files). */
  apiBase: string;
  parts: readonly ScreenPart[];
  extensions: MeExtensions;
  nav: readonly NavItem[];
  navSections: readonly NavSection[];
  favoriteKinds: Readonly<Record<string, FavoriteKind>>;
  settings: readonly SettingsItem[];
  /** Buttons the parts put in other parts' detail headers, by kind. */
  headerActions: Readonly<Record<string, readonly HeaderAction[]>>;
  /** Marks the parts put on other parts' list rows, by kind. */
  rowMarks?: Readonly<Record<string, readonly RowMark[]>>;
  /** How the parts show a thing of their kinds in a side view, by ref kind. */
  sideViews: Readonly<Record<string, ComponentType<SideViewProps>>>;
  workspaceFilter: WorkspaceFilter;
  /** Where each list was, per history entry (shell/list-memory.ts). */
  listMemory: ListMemory;
  /** The shortcut sheet's open state. */
  sheet: SheetState;
}

export const MeAppContext = createContext<MeApp | null>(null);

export function useMeApp(): MeApp {
  const app = useContext(MeAppContext);
  if (!app) throw new Error("useMeApp outside the Me app");
  return app;
}

export const useApi = (): Api => useMeApp().api;
